import { Component, Event, EventEmitter, h, Element as HostElement, Prop, State, Watch } from '@stencil/core'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/menu?tab=designer).
 */
@Component({
  tag: 'br-menu-list',
  styleUrl: 'menu-list.scss',
  shadow: true,
})
export class MenuList {
  /**
   * Ícone exibido no item do menu.
   * Utilize esta propriedade para definir o ícone que será mostrado ao lado do label.
   */
  @Prop() icon: string

  /**
   * Texto do label do item do menu.
   * Utilize esta propriedade para definir o texto que será exibido no item do menu.
   */
  @Prop() label: string

  /**
   * Nível do menu na hierarquia de navegação.
   * Utilizado para determinar o comportamento do menu (acordeão para nível 0, drill-down para níveis superiores).
   */
  @Prop() menuLevel: number = 0

  /**
   * Define se o item deve exibir um divisor visual.
   * Quando ativado, adiciona uma linha separadora para organizar visualmente os itens do menu.
   */
  @Prop() divider: boolean = false

  /**
   * Define se o menu-list inicia expandido.
   * Útil para manter agrupamentos abertos por padrão no menu contextual.
   */
  @Prop({ mutable: true }) expanded: boolean = false

  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @HostElement() private el: HTMLElement

  // Estado interno para controle de expansão
  @State() private internalExpanded: boolean = false

  @Watch('expanded')
  handleExpandedChange(newValue: boolean) {
    this.internalExpanded = newValue
  }

  /**
   * Quando verdadeiro, suprime toda a renderização do conteúdo no shadow DOM.
   * Utilizado para cenários onde a lista deve ficar oculta.
   */
  @State() shouldHide: boolean = false

  /**
   * Emitido quando um folder é expandido ou recolhido no nível 0 (comportamento acordeão).
   */
  @Event() folderToggled: EventEmitter<boolean>

  /**
   * Emitido quando navegando para um submenu nos níveis 1+ (comportamento drill-down).
   */
  @Event() navigateToSubmenu: EventEmitter<{ element: HTMLElement; label: string }>

  componentWillLoad() {
    this.internalExpanded = this.expanded
  }

  private onItemClick = () => {
    this.toggleFolder()
  }

  private onItemKeyDown = (ev: KeyboardEvent) => {
    this.handleKeyDown(ev)
  }

  private toggleFolder() {
    // Aplica a regra principal: comportamento por nível
    if (this.menuLevel === 0) {
      // Camada 1: Comportamento de acordeão
      this.internalExpanded = !this.internalExpanded
      this.expanded = this.internalExpanded
      this.folderToggled.emit(this.internalExpanded)
      if (this.internalExpanded) {
        // Após expandir, foca no primeiro item visível do submenu
        requestAnimationFrame(() => this.focusFirstChildAnchor())
      }
    } else {
      // Camada 2+: Comportamento drill-down
      this.navigateToSubmenu.emit({
        element: this.el,
        label: this.label,
      })
    }
  }

  private handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault()
      this.toggleFolder()
    }
  }

  private isVisibleElement(el: Element | null): el is HTMLElement {
    if (el == null) return false
    const htmlEl = el as HTMLElement
    if (!htmlEl.isConnected) return false
    if (htmlEl.hasAttribute('hidden')) return false
    const rects = htmlEl.getClientRects()
    return rects.length > 0
  }

  private focusFirstChildAnchor() {
    const slotEl = this.el.shadowRoot?.querySelector('ul slot') as HTMLSlotElement | null
    const assigned = slotEl?.assignedElements?.() || []
    for (const element of assigned) {
      if (element.tagName === 'BR-MENU-ITEM') {
        const anchor = (element as HTMLElement).shadowRoot?.querySelector('a') as HTMLElement | null
        if (this.isVisibleElement(element) && this.isVisibleElement(anchor)) {
          anchor.focus()
          return
        }
      } else if (element.tagName === 'BR-MENU-LIST') {
        const subHeader = (element as HTMLElement).shadowRoot?.querySelector('br-menu-item') as HTMLElement | null
        const subAnchor = subHeader?.shadowRoot?.querySelector('a') as HTMLElement | null
        if (this.isVisibleElement(element) && this.isVisibleElement(subAnchor)) {
          subAnchor.focus()
          return
        }
      }
    }
  }

  render() {
    // Renderização condicional baseada no nível
    const isLevel0 = this.menuLevel === 0
    const classes = {
      'menu-folder': true,
      'drop-menu': isLevel0, // Level 0: dropdown style
      'side-menu': !isLevel0, // Level 1+: side menu style
      active: this.internalExpanded,
      'divider-list': this.divider,
    }

    if (this.shouldHide) {
      return null
    }

    return (
      <div class={classes} role="none">
        <br-menu-item
          icon={this.icon}
          onClick={this.onItemClick}
          onKeyDown={this.onItemKeyDown}
          expanded={this.internalExpanded}
          display-mode={isLevel0 ? 'accordion' : 'drill-down'}
        >
          {this.label}
        </br-menu-item>

        <ul
          role="tree"
          aria-label={this.label}
          style={isLevel0 ? { display: this.internalExpanded ? 'block' : 'none' } : {}}
        >
          {isLevel0 ? (
            <li>
              <slot></slot>
            </li>
          ) : (
            <slot></slot>
          )}
        </ul>
      </div>
    )
  }
}
